/**
 * Component Detection Utilities
 * Detects and parses AI-generated components in HTML content
 */

export interface DetectedComponent {
  type: 'learning-objectives' | 'knowledge-check' | 'code-snippet' | 'example-box' | 'callout';
  element: Element;
  position: {
    start: number;
    end: number;
  };
  data: any;
}

export interface LearningObjective {
  title: string;
  items: string[];
}

export interface KnowledgeCheck {
  checkpointType: string;
  question: string;
  answerId: string;
  answer: string;
  extension?: string;
}

export interface CodeSnippet {
  language: string;
  code: string;
  hasLineNumbers: boolean;
  hasSyntaxHighlighting: boolean;
}

export interface ExampleBox {
  title: string;
  content: string;
}

export interface Callout {
  type: 'tip' | 'warning' | 'note' | 'info';
  title: string;
  content: string;
}

/**
 * Detect all AI-generated components in HTML
 */
export function detectComponents(html: string): DetectedComponent[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const components: DetectedComponent[] = [];

  // Detect learning objectives
  doc.querySelectorAll('.learning-objectives').forEach((el) => {
    components.push({
      type: 'learning-objectives',
      element: el,
      position: { start: 0, end: 0 }, // Would need actual position calculation
      data: extractLearningObjectives(el),
    });
  });

  // Detect knowledge checks
  doc.querySelectorAll('.learning-checkpoint').forEach((el) => {
    components.push({
      type: 'knowledge-check',
      element: el,
      position: { start: 0, end: 0 },
      data: extractKnowledgeCheck(el),
    });
  });

  // Detect code snippets
  doc.querySelectorAll('.skillzen-code-snippet').forEach((el) => {
    components.push({
      type: 'code-snippet',
      element: el,
      position: { start: 0, end: 0 },
      data: extractCodeSnippet(el),
    });
  });

  // Detect example boxes
  doc.querySelectorAll('.example-box').forEach((el) => {
    components.push({
      type: 'example-box',
      element: el,
      position: { start: 0, end: 0 },
      data: extractExampleBox(el),
    });
  });

  return components;
}

/**
 * Strip leading emojis, icons, bullets, and checkmarks from text
 */
function stripLeadingIcons(text: string): string {
  // Remove common leading icons: emojis, checkmarks, bullets, arrows, etc.
  // This regex matches: emojis, ✓ ✔ ☑ • ◦ ‣ ⁃ → ► ▸ ▹ ★ ☆ ● ○ ◆ ◇ and other symbols
  // Also includes Unicode escapes for checkmarks (\u2713, \u2714, \u2611, \u2705)
  return text
    .replace(/^[\s]*[✓✔☑✅⬜☐•◦‣⁃→►▸▹★☆●○◆◇🎯📚🎓📝✔️💡🔑⚡✨🌟💫🎁📍\u2713\u2714\u2611\u2705]+[\s]*/g, '')
    .trim();
}

/**
 * Extract learning objectives data
 */
export function extractLearningObjectives(element: Element): LearningObjective {
  const title = element.querySelector('h2, h3')?.textContent || 'Learning Objectives';
  const items: string[] = [];

  element.querySelectorAll('li').forEach((li) => {
    const text = li.textContent?.trim();
    if (text) {
      // Strip any leading icons/emojis from the text
      const cleanText = stripLeadingIcons(text);
      if (cleanText) {
        items.push(cleanText);
      }
    }
  });

  return { title, items };
}

/**
 * Extract knowledge check data
 */
export function extractKnowledgeCheck(element: Element): KnowledgeCheck {
  const checkpointTypeEl = element.querySelector('.checkpoint-type');
  let checkpointType = 'Knowledge Check';

  if (checkpointTypeEl) {
    // Clone and remove the icon span to get only the title text
    const clone = checkpointTypeEl.cloneNode(true) as Element;
    const iconSpan = clone.querySelector('span');
    if (iconSpan) {
      iconSpan.remove();
    }
    // Get the remaining text (title only)
    checkpointType = clone.textContent?.trim() || 'Knowledge Check';
  }

  const question = element.querySelector('.question')?.textContent?.trim() || '';
  const answerEl = element.querySelector('.answer');
  const answerId = answerEl?.id || '';

  // Extract answer text (exclude the "Answer:" label)
  let answer = '';
  if (answerEl) {
    // Clone the element to manipulate
    const clone = answerEl.cloneNode(true) as Element;

    // Remove the "Answer:" strong tag
    const strongEl = clone.querySelector('strong');
    if (strongEl) {
      strongEl.remove();
    }

    // Remove extension div to get only the main answer
    const extensionEl = clone.querySelector('.extension');
    if (extensionEl) {
      extensionEl.remove();
    }

    // Get remaining text
    answer = clone.textContent?.trim() || '';
  }

  // Extract extension if present
  const extensionEl = element.querySelector('.extension');
  let extension: string | undefined;
  if (extensionEl) {
    // Clone to manipulate
    const clone = extensionEl.cloneNode(true) as Element;

    // Remove the strong tag with "Going Deeper:"
    const strongEl = clone.querySelector('strong');
    if (strongEl) {
      strongEl.remove();
    }

    extension = clone.textContent?.replace(/^💡\s*/, '').trim();
  }

  return {
    checkpointType,
    question,
    answerId,
    answer,
    extension,
  };
}

/**
 * Extract code snippet data
 */
export function extractCodeSnippet(element: Element): CodeSnippet {
  const headerEl = element.querySelector('.skillzen-code-header');
  const language = headerEl?.textContent?.match(/\b(JAVASCRIPT|PYTHON|HTML|CSS|JAVA|SWIFT|SQL)\b/i)?.[0] || 'code';

  const codeEl = element.querySelector('code');
  let code = '';

  // Check if it has line numbers and syntax highlighting
  const hasLineNumbers = !!element.querySelector('.skillzen-code-line[data-line]');
  const hasSyntaxHighlighting = !!element.querySelector('.skillzen-keyword, .skillzen-string, .skillzen-function');

  if (hasLineNumbers) {
    // Extract code from line-by-line structure
    element.querySelectorAll('.skillzen-code-line').forEach((line, index) => {
      if (index > 0) code += '\n';
      code += line.textContent || '';
    });
  } else {
    code = codeEl?.textContent || '';
  }

  return {
    language: language.toLowerCase(),
    code: code.trim(),
    hasLineNumbers,
    hasSyntaxHighlighting,
  };
}

/**
 * Extract example box data
 */
export function extractExampleBox(element: Element): ExampleBox {
  // Try to find the title from h3 or the header div
  let title = 'Example';
  const h3 = element.querySelector('h3');
  if (h3) {
    title = h3.textContent?.trim() || 'Example';
  }

  // Extract content (the div after the header)
  let content = '';
  const contentDiv = element.querySelector('div:last-child');
  if (contentDiv && !contentDiv.querySelector('h3')) {
    content = contentDiv.textContent?.trim() || '';
  } else {
    // Fallback: get all text content except the title
    const clone = element.cloneNode(true) as Element;
    const titleEl = clone.querySelector('h3');
    if (titleEl) titleEl.remove();
    const headerDiv = clone.querySelector('div:first-child');
    if (headerDiv && headerDiv.querySelector('span')) headerDiv.remove();
    content = clone.textContent?.trim() || '';
  }

  return { title, content };
}

/**
 * Extract callout data
 */
export function extractCallout(element: Element): Callout {
  // Determine callout type from class or data attribute
  let type: Callout['type'] = 'info';
  const dataComponent = element.getAttribute('data-component');

  if (dataComponent) {
    const typeMatch = dataComponent.match(/callout-(tip|warning|note|info)/);
    if (typeMatch) {
      type = typeMatch[1] as Callout['type'];
    }
  } else if (element.classList.contains('callout-tip')) {
    type = 'tip';
  } else if (element.classList.contains('callout-warning')) {
    type = 'warning';
  } else if (element.classList.contains('callout-note')) {
    type = 'note';
  } else if (element.classList.contains('callout-info')) {
    type = 'info';
  }

  // Extract title
  let title = '';
  const titleDiv = element.querySelector('div[style*="font-weight: 600"], div[style*="font-weight:600"]');
  if (titleDiv) {
    title = titleDiv.textContent?.trim() || '';
  }

  // Extract content
  let content = '';
  const contentDivs = element.querySelectorAll('div');
  // The content is usually in the second-to-last div (inside the flex container)
  if (contentDivs.length >= 2) {
    const lastContentDiv = contentDivs[contentDivs.length - 1];
    if (!lastContentDiv.querySelector('div')) {
      content = lastContentDiv.textContent?.trim() || '';
    }
  }

  return { type, title, content };
}

/**
 * Check if HTML has learning objectives
 */
export function hasLearningObjectives(html: string): boolean {
  return html.includes('learning-objectives');
}

/**
 * Check if HTML has knowledge checks
 */
export function hasKnowledgeChecks(html: string): boolean {
  return html.includes('learning-checkpoint');
}

/**
 * Check if HTML has code snippets
 */
export function hasCodeSnippets(html: string): boolean {
  return html.includes('skillzen-code-snippet');
}

/**
 * Check if HTML has example boxes
 */
export function hasExampleBoxes(html: string): boolean {
  return html.includes('example-box');
}

/**
 * Get component statistics
 */
export function getComponentStats(html: string): {
  learningObjectives: number;
  knowledgeChecks: number;
  codeSnippets: number;
  exampleBoxes: number;
  total: number;
} {
  const components = detectComponents(html);

  return {
    learningObjectives: components.filter((c) => c.type === 'learning-objectives').length,
    knowledgeChecks: components.filter((c) => c.type === 'knowledge-check').length,
    codeSnippets: components.filter((c) => c.type === 'code-snippet').length,
    exampleBoxes: components.filter((c) => c.type === 'example-box').length,
    total: components.length,
  };
}
